<template>
  <div :id="props.id || `product-banner-${random_id}`"
       class="relative w-max over"
       @click="logDynamicData"
       >
    <TsImage v-if="props.backgroundImage
              && props.backgroundImage.image"
             :src="props.backgroundImage.image"
             :width="props.backgroundImage.width"
             :height="props.backgroundImage.height" 
              loading="lazy"/>

    <div v-if="props.brandLogo"
         :class="twMerge(
             'absolute top-0 p-5',
             props.brandLogoAlign === 'left' && 'left-0',
             props.brandLogoAlign === 'center' && 'left-1/2 -translate-x-1/2',
             props.brandLogoAlign === 'right' && 'right-0'
         )"
    >
      <TsImage v-if="props.brandLogo && props.brandLogo.image"
               :src="props.brandLogo.image"
               :width="props.brandLogo.width"
               :height="props.brandLogo.height" 
                loading="lazy"
               />
    </div>

    <div class="w-full h-full absolute top-0 left-0 p-5">
      <TsMedia
          :append-class="twMerge(
              'h-full',
              props.reverseLayout && 'flex-row-reverse'
          )">
        <TsMediaContent
            :append-class="twMerge(
              'flex flex-col justify-center',
              props.contentAlign === 'left' && 'items-start',
              props.contentAlign === 'center' && 'items-center',
              props.contentAlign === 'right' && 'items-end'
            )">
          <TsTypography
              :append-class="twMerge(
                  'text-[36px] font-black leading-none mb-2',
                  props.title.color === 'warning' && 'text-[#FFBD16]',
                  props.title.color === 'primary' && 'text-primary',
                  props.title.color === 'black' && 'text-idle-black',
                  props.title.color === 'white' && 'text-white'
              )"
          >
            {{ props.title.text }}
          </TsTypography>
          <template v-if="props.showPrice && product_price">
            <TsTypography
                :append-class="twMerge(
                    'text-[36px] text-white font-bold leading-tight',
                    props.priceColor === 'warning' && 'text-[#FFBD16]',
                    props.priceColor === 'primary' && 'text-primary',
                    props.priceColor === 'black' && 'text-idle-black',
                    props.priceColor === 'white' && 'text-white'
                )"
            >
              {{ product_price }}
            </TsTypography>
            <TsTypography
                :append-class="twMerge(
                    'text-white font-semibold mb-4 leading-none',
                    props.priceColor === 'warning' && 'text-[#FFBD16]',
                    props.priceColor === 'primary' && 'text-primary',
                    props.priceColor === 'black' && 'text-idle-black',
                    props.priceColor === 'white' && 'text-white'
                )"
            >
              {{ rootStore.isIncVat ? 'Incl.' : 'Excl.' }} {{useTranslation('vat' ,'btw')}}
            </TsTypography>
          </template>

          <TsButton
              v-if="props.buttonLabel"
              :label="props.buttonLabel"
              append-class="w-max"
              size="sm"
          />
        </TsMediaContent>
        <TsMediaEnd v-if="props.foregroundImage &&
        props.foregroundImage.image"
                    append-class="flex">
          <TsImage class="mt-auto relative -bottom-11"
                   :src="props.foregroundImage.image"
                   :width="props.foregroundImage.width"
                   :height="props.foregroundImage.height" 
                    loading="lazy"/>
        </TsMediaEnd>
      </TsMedia>
    </div>
  </div>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { useTranslation } from "~/composables/useTranslation";

const TsMediaContent = defineAsyncComponent(
    () => import('~/components/organisms/TsMediaContent.vue')
);
const TsMediaEnd = defineAsyncComponent(
    () => import('~/components/organisms/TsMediaEnd.vue')
);

const rootStore = useRootStore();

type Image = {
  image?: string;
  width?: string;
  height?: string;
}

type Typography = {
  text?: string;
  color?: 'black' | 'white' | 'primary' | 'warning'
}

type Props = {
  id?: string;
  productId?: string;
  backgroundImage?: Image;
  brandLogo?: Image;
  brandLogoAlign?: 'left' | 'center' | 'right';
  foregroundImage?: Image;
  title?: Typography;
  buttonLabel?: string;
  contentAlign?: 'left' | 'center' | 'right';
  showPrice?: boolean;
  priceColor?: 'black' | 'white' | 'primary' | 'warning';
  reverseLayout?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productId: '29688',
  backgroundImage: () => ({
    image: '/images/banner-background-image.webp',
    width: '720',
    height: '363'
  }),
  brandLogo: () => ({
    image: '/images/banner-brand-logo.png',
    width: '170',
    height: '28'
  }),
  brandLogoAlign: 'right',
  foregroundImage: () => ({
    image: '/images/banner-foreground-image.webp',
    width: '400',
    height: '249'
  }),
  title: () => ({
    text: 'BUNDLE OFFER',
    color: 'warning'
  }),
  buttonLabel: 'Know more',
  showPrice: true,
  reverseLayout: false
})

const product_id = computed(() => props.productId);
const product_data = ref();
const product_price = computed(() => {
  if(!props.showPrice) return null;
  return rootStore.isIncVat ? product_data.value?.prices?.formatted?.gross : product_data.value?.prices?.formatted?.net;
})

const getProductData = async (id: string) => {
  const data = await useAjaxEcom(
      '/products/getProducts',
      {
        method: 'post',
        params: {
          "products": [id]
        }
      }
  );

  return data?.data[0];
}

const random_id = ref("");
onMounted(async () => {
  random_id.value = useRandomUUID();
  if(props.showPrice) {
    product_data.value = await getProductData(product_id.value);
    bannerView(product_data?.value, "banner product")
  }
});

const stopBannerProductCodeWatcher = watch(product_id, async () => {
  if(!props.showPrice) return;
  product_data.value = await getProductData(product_id.value)
})

// GTM Log FOR banner click
const logDynamicData = () => {
  bannerClick(product_data?.value, "banner product")
};

onUnmounted(() => {
  stopBannerProductCodeWatcher();
})
</script>